/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Route, Switch, Redirect } from 'react-router-dom';
import cx from 'classnames';

import {
    TextButton,
    Text,
    Spacer,
    Alert,
    CloseButton,
    Heading,
} from '@zillow/constellation';

import { Container, Col, Row } from '@zillow/zrs-comet';

// Constants
import { BILLING_PAGE } from 'app/shared/constants/RouteConstants';
import ModalConstants from 'app/shared/constants/ModalConstants';
import LoadingConstants from 'app/shared/constants/LoadingConstants';

// Actions
import ModalActions from 'app/shared/actions/ModalActions';
import BillingActions from 'app/shared/actions/BillingActions';

// Components
import PermissionCheck from 'app/shared/components/PermissionCheck';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import { withErrorBoundary } from 'app/shared/components/ErrorBoundary';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

// Selectors
import { RoutingSelectors, UserSelectors, AppSelectors } from 'app/shared/selectors';

import { RMWContext } from 'app/shared/providers/RMWContext';

import PaymentDuePage from 'app/shared/containers/PaymentDuePage';
import PaidInvoicesPage from 'app/shared/containers/PaidInvoicesPage';
import PaymentSettingsPage from 'app/shared/containers/PaymentSettingsPage';
import SearchBox from 'app/shared/components/SearchBox';

import billingUtils from 'app/shared/utils/billingUtils';
import accessUtils from 'app/shared/utils/accessUtils';

import { StyledContainer } from './StyledComponents';

// Stylesheets
import './style.scss';

const DEFAULT_PAGE_TITLE = 'Billing';

const TabList = {
    paid: 'paid',
    due: 'due',
    paymentSettings: 'paymentSettings',
};

const propTypes = {
    router: PropTypes.object.isRequired,
};

const contextTypes = {
    paths: PropTypes.object,
};

const BillingPage = ({
    router,
    user,
    isScreenMobile,
    currentInvoiceLoadingState,
    displaySuccessMessage,
}) => {
    const { paths } = React.useContext(RMWContext);
    const dispatch = useDispatch();

    React.useEffect(() => {
        return () => {
            dispatch(BillingActions.setDisplayPaymentSuccess(false));
            dispatch(BillingActions.initUserSettings());
        };
    }, []);


    const loadTab = (tab) => {
        const baseDetailsUrl = paths.billingPage;
        const url = `${baseDetailsUrl}/${tab}`;
        return window.router.transitionTo(url);
    };

    const getActiveTab = () => {
        // Expecting path to be [baseUrl]/rental-manager/billing/*something*
        const tabName = router.location.pathname.split('/')[3];

        switch (tabName) {
            case paths.billingDue:
                return paths.billingDue;
            case paths.billingPaid:
                return paths.billingPaid;
            case 'paymentSettings':
                return 'paymentSettings';
            default:
                return paths.billingDue;
        }
    };

    const getSectionUrl = (section) => {
        const baseDetailsUrl = paths.billingPage;
        return `${baseDetailsUrl}/${section}([?].*)?`;
    };

    const renderChildRoutes = () => {
        const baseDetailsUrl = paths.billingPage;
        const isOnlyNucleusUser = billingUtils.isOnlyNucleusUser(user);

        // child paths
        const childPathDue = getSectionUrl(paths.billingDue);
        const childPathPaid = getSectionUrl(paths.billingPaid);
        const childPathPaymentSettings = getSectionUrl('paymentSettings');
        return (
            <Switch>
                <Route
                    render={() => {
                        if (isOnlyNucleusUser) {
                            return <Redirect to={`${baseDetailsUrl}/${paths.billingPaid}`} />;
                        }
                        return <Route component={PaymentDuePage} path={childPathDue} />;
                    }}
                    path={childPathDue}
                />
                <Route component={PaidInvoicesPage} path={childPathPaid} />
                <Route component={PaymentSettingsPage} path={childPathPaymentSettings} />
            </Switch>
        );
    };

    const handlePayInfo = () => {
        dispatch(ModalActions.showModal({
            modalType: ModalConstants.PAYMENT_INFO_MODAL,
        }));
    };

    const handleSearch = (searchTerm) => {
        dispatch(BillingActions.search(searchTerm));
    };

    const isSearchEnabled = !billingUtils.isOnlyNucleusUser(user);

    const renderPaymentInfo = () => {
        if (isScreenMobile) return null;
        return (
            <li
                className="TabMenuItem-info TabMenuItem-Right TabMenuItem-note"
            >
                <Text fontType="bodySmall">
                    *Only credit card payment is supported for online payments right now.
                    For other types of payments, please follow the instructions
                    {' '}
                    <TextButton onClick={handlePayInfo} fontType="bodySmall">here</TextButton>
                </Text>
            </li>
        );
    };

    const renderSubNav = () => {
        const activeTab = getActiveTab();
        const paymentsEnabled = accessUtils.canAccessInvoicePayments({ user });

        return (
            <Container className="BillingPage-SubNav">
                <Spacer paddingX="lg" marginY="xs">
                    <ul className="BillingPage-SubNav-list">
                        <li
                            className={cx('TabMenuItem-links', {
                                'TabMenuItem-active': activeTab === TabList.due,
                            })}
                            onClick={() => loadTab(paths.billingDue)}
                        >
                            <Text
                                fontType="body"
                                className={cx({ 'Utils-primary': activeTab === TabList.due })}
                            >
                                Payment Due
                            </Text>
                        </li>
                        <li
                            className={cx('TabMenuItem-links', {
                                'TabMenuItem-active': activeTab === TabList.paid,
                            })}
                            onClick={() => loadTab(paths.billingPaid)}
                        >
                            <Text
                                fontType="body"
                                className={cx({ 'Utils-primary': activeTab === TabList.paid })}
                            >
                                History
                            </Text>
                        </li>
                        {paymentsEnabled && (
                            <li
                                className={cx('TabMenuItem-links', {
                                    'TabMenuItem-active': activeTab === TabList.paymentSettings,
                                })}
                                onClick={() => loadTab('paymentSettings')}
                            >
                                <Text
                                    fontType="body"
                                    className={cx({ 'Utils-primary': activeTab === TabList.paymentSettings })}
                                >
                                    Settings
                                </Text>
                            </li>
                        )}
                        {paymentsEnabled ?
                            renderPaymentInfo()
                            : (
                                <li
                                    className="TabMenuItem-links TabMenuItem-Right"
                                    onClick={handlePayInfo}
                                >
                                    <Text
                                        fontType="body"
                                        className="Utils-primary"
                                    >
                                        How to pay?
                                    </Text>
                                </li>
                            )
                        }
                    </ul>
                </Spacer>
            </Container>
        );
    };

    const renderContent = () => {
        const isOnlyNucleusUser = billingUtils.isOnlyNucleusUser(user);
        return (
            <PermissionCheck route={BILLING_PAGE}>
                <Container className="BillingPage-container">
                    {displaySuccessMessage && (
                        <Row>
                            <Col xs={12}>
                                <Alert
                                    isOpen={true}
                                    appearance="success"
                                    body="Thank you for your payment!"
                                    onClose={() => dispatch(BillingActions.setDisplayPaymentSuccess(false))}
                                    closeButton={<CloseButton />}
                                />
                            </Col>
                        </Row>
                    )}
                    <StyledContainer className="BillingPage-card">
                        <Row>
                            <Col xs={7}>
                                <Spacer paddingX="lg">
                                    <Heading level="2" fontFamily="serif">{DEFAULT_PAGE_TITLE}</Heading>
                                </Spacer>
                            </Col>
                            <Col xs={5}>
                                <Spacer paddingX="lg">
                                    {isSearchEnabled && (
                                        <SearchBox
                                            onSubmit={handleSearch}
                                            placeholder="Search by Invoice No"
                                            isSearching={currentInvoiceLoadingState === LoadingConstants.LOADING}
                                        />
                                    )}
                                </Spacer>
                            </Col>
                        </Row>
                        {!isOnlyNucleusUser && renderSubNav()}
                        {renderChildRoutes()}
                    </StyledContainer>
                </Container>
            </PermissionCheck>
        );
    };


    return (
        <AuthCheckWrapper requireStrongAuthentication={false}>
            <div className="BillingPage">
                <HeadWrapper title={DEFAULT_PAGE_TITLE} />
                {renderContent()}
            </div>
        </AuthCheckWrapper>
    );
};

const mapStateToProps = state => ({
    router: RoutingSelectors.makeRouting(state),
    currentInvoiceLoadingState: state.billing.invoice.currentInvoiceLoadingState,
    user: UserSelectors.makeUser(state),
    isScreenMobile: AppSelectors.makeIsScreenMobile(state),
    displaySuccessMessage: state.billing.payment.displaySuccessMessage,
});

BillingPage.contextTypes = contextTypes;
BillingPage.propTypes = propTypes;

export default withErrorBoundary(connect(mapStateToProps)(BillingPage));
